// Extracted from original/src/App.tsx:33771-34270 (`activeTab === "debts"`, ADR-08 §2.1 "copy, then cut logic"):
//   header cards (total owed / total held)            :33779-33833
//   sticky bar: segmented filter + transfer / dual    :33834-33946
//              sort CustomSelect + search             :33948-33996
//   table head / rows / row actions                   :33997-34269
// Kept: every class string, icon and size, the motion frame, the "د.ع" / "$" unit markup, the old sort ids and the
// column header switch to "تاريخ ونوع آخر حركة" under the recent-activity sort.
// Cut: `dashboardData.totalOwed / totalCredit` (stored balances) → `totals` from the ledger (D-013, R-17, R-21);
// `filteredDebts` / `customerActivityMap` (name-matched movements, R-22) → `rows` / `activity` from the read model
// (the container filters, sorts and formats); `formatNumber` → preformatted labels; `new Date().toLocale…` → labels
// in the office time zone (D-064); `showExchangeRate` (per-browser) → `usdHidden` (office setting, D-066);
// `hasPermission(currentUser, …)` → `canEdit` / `canArchive` (D-034, R-25); `fetchHistory` / `handleShareAccountStatement`
// / `setAdjustingDebt` / `setSettlingDebt` / `setCustomerTransferData` / `setConvertCurrencyCustomer` /
// `setEditingCustomer` / `handleDeleteDebt` → callbacks; the "#" column shows the office's running number (`number`)
// and the chip the visible account number (D-017; the old `1000 + id` comparison is gone).
// Changed by decision: the trash button archives (D-072; its title says so); a "الحسابات المؤرشفة" button appears in
// the action group when archived accounts exist (D-072 "Archived accounts" list — V-20).
// Added: data-testid attributes.
import { motion } from 'motion/react';
import { Activity, Archive, ArrowDownLeft, ArrowLeftRight, ArrowUpDown, ArrowUpRight, Clock, Edit2, FileText, Printer, Search, Trash2, X } from 'lucide-react';
import { CustomSelect } from '../components/CustomSelect.js';
import { useI18n } from '../i18n-context.js';

export type CaFilter = 'all' | 'debt' | 'trust';
export type CaSort =
  | 'default' | 'recent_activity' | 'highest_balance' | 'lowest_balance' | 'alphabetical' | 'alphabetical_desc'
  | 'account_number_asc' | 'account_number_desc' | 'created_desc' | 'created_asc';
export const CA_SORTS: readonly CaSort[] = [
  'default', 'recent_activity', 'highest_balance', 'lowest_balance', 'alphabetical', 'alphabetical_desc',
  'account_number_asc', 'account_number_desc', 'created_desc', 'created_asc',
];

export interface CaRow {
  readonly id: string;
  /** Old "#" column (office running number). */
  readonly numberLabel: string;
  readonly name: string;
  readonly accountNumber: string;
  readonly phone: string | null;
  readonly address: string | null;
  readonly kind: 'debt' | 'trust';
  /** "1,000,000 د.ع" (IQD line). */
  readonly iqdLabel: string;
  /** "500 $" when the USD line is not zero and USD is shown; otherwise null (old `showExchangeRate && amount_usd !== 0`). */
  readonly usdLabel: string | null;
  /** Latest activity (old `customerActivityMap`), or null → `createdLabel`. */
  readonly activity: { readonly dateLabel: string; readonly label: string; readonly title: string } | null;
  readonly createdLabel: string;
}

export interface CustomerAccountsProps {
  readonly totals: { readonly owedIqdLabel: string; readonly owedUsdLabel: string | null; readonly creditIqdLabel: string; readonly creditUsdLabel: string | null };
  readonly counts: { readonly all: number; readonly debt: number; readonly trust: number };
  readonly filter: CaFilter;
  readonly onFilterChange: (f: CaFilter) => void;
  readonly sort: CaSort;
  readonly onSortChange: (s: CaSort) => void;
  readonly search: string;
  readonly onSearchChange: (s: string) => void;
  readonly rows: readonly CaRow[];
  readonly usdHidden: boolean;
  readonly canEdit: boolean;
  readonly canArchive: boolean;
  readonly archivedCount: number;
  readonly onOpenArchived: () => void;
  readonly onTransfer: () => void;
  readonly onDualStatement: () => void;
  readonly onStatement: (id: string) => void;
  readonly onSharePdf: (id: string) => void;
  readonly onTransferFrom: (id: string) => void;
  readonly onConvert: (id: string) => void;
  readonly onAdjust: (id: string, action: 'add' | 'withdraw') => void;
  readonly onSettle: (id: string) => void;
  readonly onEdit: (id: string) => void;
  readonly onArchive: (id: string) => void;
}

export function CustomerAccounts(props: CustomerAccountsProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const debtFilter = props.filter;
  const debtSortBy = props.sort;
  const searchTerm = props.search;
  const showExchangeRate = !props.usdHidden;

  return (
    <motion.div
      key="debts"
      data-testid="customers-screen"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-8"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white dark:bg-[#0d1117] p-6 rounded-3xl border border-gray-100 dark:border-[#30363d] flex items-center gap-5 shadow-sm">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 dark:bg-rose-900/10 flex items-center justify-center text-rose-600 dark:text-rose-400">
            <ArrowUpRight size={28} />
          </div>
          <div className={`${lang === "ar" ? "text-right" : "text-left"} flex-1`}>
            <h4 className="font-black text-lg dark:text-white uppercase tracking-tight">
              {t('totalOwed')}
            </h4>
            <div className="flex flex-col">
              <p className="text-2xl font-black text-rose-600 dark:text-rose-400 font-mono dark:digital-glow" data-testid="customers-total-owed">
                {props.totals.owedIqdLabel} د.ع
              </p>
              {showExchangeRate && props.totals.owedUsdLabel !== null && (
                <p className="text-lg font-black text-blue-600 dark:text-blue-400 font-mono">
                  {props.totals.owedUsdLabel}{" "}
                  $
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-[#0d1117] p-6 rounded-3xl border border-gray-100 dark:border-[#30363d] flex items-center gap-5 shadow-sm">
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-900/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
            <ArrowDownLeft size={28} />
          </div>
          <div className={`${lang === "ar" ? "text-right" : "text-left"} flex-1`}>
            <h4 className="font-black text-lg dark:text-white uppercase tracking-tight">
              {t('totalCredit')}
            </h4>
            <div className="flex flex-col">
              <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono dark:digital-glow" data-testid="customers-total-credit">
                {props.totals.creditIqdLabel} د.ع
              </p>
              {showExchangeRate && props.totals.creditUsdLabel !== null && (
                <p className="text-lg font-black text-blue-600 dark:text-blue-400 font-mono">
                  {props.totals.creditUsdLabel}{" "}
                  $
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="sticky top-0 z-30 bg-[#f8fafc]/95 dark:bg-[#05070a]/95 backdrop-blur-md py-2 -mx-4 px-4 md:-mx-8 md:px-8 border-b border-gray-200/75 dark:border-[#30363d] shadow-sm transition-all flex flex-wrap items-center justify-between gap-2.5 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          {/* Segmented Filter Control */}
          <div className="inline-flex items-center bg-gray-200/80 dark:bg-[#161b22] p-0.5 rounded-xl border border-gray-200 dark:border-[#30363d]">
            <button
              onClick={() => props.onFilterChange("all")}
              data-testid="customers-filter-all"
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all inline-flex items-center gap-1.5 ${
                debtFilter === "all"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              <span>{t('cust.filter_all')}</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                debtFilter === "all"
                  ? "bg-white/20 text-white"
                  : "bg-gray-300/70 dark:bg-gray-700/70 text-gray-700 dark:text-gray-300"
              }`}>
                {props.counts.all}
              </span>
            </button>
            <button
              onClick={() => props.onFilterChange("debt")}
              data-testid="customers-filter-debt"
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all inline-flex items-center gap-1.5 ${
                debtFilter === "debt"
                  ? "bg-rose-600 text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-rose-600 dark:hover:text-rose-400"
              }`}
            >
              <span>{t('owed')}</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                debtFilter === "debt"
                  ? "bg-white/20 text-white"
                  : "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400"
              }`}>
                {props.counts.debt}
              </span>
            </button>
            <button
              onClick={() => props.onFilterChange("trust")}
              data-testid="customers-filter-trust"
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all inline-flex items-center gap-1.5 ${
                debtFilter === "trust"
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400"
              }`}
            >
              <span>{t('credit')}</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                debtFilter === "trust"
                  ? "bg-white/20 text-white"
                  : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
              }`}>
                {props.counts.trust}
              </span>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={props.onTransfer}
              data-testid="customers-transfer"
              className="px-2.5 py-1.5 rounded-xl text-xs font-black transition-all bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white shadow-sm flex items-center gap-1.5 whitespace-nowrap h-8"
              title={t('cust.transfer_title')}
            >
              <ArrowLeftRight size={13} />
              <span>{t('cust.transfer_short')}</span>
            </button>
            <button
              onClick={props.onDualStatement}
              data-testid="customers-dual"
              className="px-2.5 py-1.5 rounded-xl text-xs font-black transition-all bg-teal-600 hover:bg-teal-700 active:scale-95 text-white shadow-sm flex items-center gap-1.5 whitespace-nowrap h-8"
              title={t('cust.dual_short')}
            >
              <FileText size={13} />
              <span>{t('cust.dual_short')}</span>
            </button>
            {props.archivedCount > 0 && (
              <button
                onClick={props.onOpenArchived}
                data-testid="customers-archived"
                className="px-2.5 py-1.5 rounded-xl text-xs font-black transition-all bg-gray-600 hover:bg-gray-700 active:scale-95 text-white shadow-sm flex items-center gap-1.5 whitespace-nowrap h-8"
                title={t('cust.archived_title')}
              >
                <Archive size={13} />
                <span>{t('cust.archived_short', { n: props.archivedCount })}</span>
              </button>
            )}
          </div>
        </div>

        {/* Sorting & Search Controls */}
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="min-w-[170px] sm:w-56 flex-1 sm:flex-initial">
            <CustomSelect
              options={CA_SORTS.map((id) => ({ id, label: t(`cust.sort_${id}`) }))}
              value={debtSortBy}
              onChange={(val) => props.onSortChange(val as CaSort)}
              headerTitle={t('cust.sort_header')}
              searchable={false}
              size="sm"
              icon={<ArrowUpDown size={13} />}
            />
          </div>
          <div className="relative min-w-[150px] sm:w-48 flex-1 sm:flex-initial">
            <Search
              className={`absolute ${lang === "ar" ? "right-2.5" : "left-2.5"} top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#8b949e] pointer-events-none`}
              size={13}
            />
            <input
              type="text"
              data-testid="customers-search"
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => props.onSearchChange(e.target.value)}
              className={`w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl ${lang === "ar" ? "pr-7 pl-6" : "pl-7 pr-6"} py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all h-8 shadow-sm`}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => props.onSearchChange("")}
                className={`absolute ${lang === "ar" ? "left-2" : "right-2"} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200`}
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className={`w-full ${lang === "ar" ? "text-right" : "text-left"} border-collapse`} data-testid="customers-table">
          <thead>
            <tr className="text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
              <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">
                #
              </th>
              <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">
                {t('customer')}
              </th>
              <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">
                {t('phone')}
              </th>
              <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">
                {t('address')}
              </th>
              <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">
                {t('amount')}
              </th>
              <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">
                {t('status')}
              </th>
              <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">
                {debtSortBy === "recent_activity" ? t('cust.last_activity_header') : t('date')}
              </th>
              <th className="pb-4 font-bold uppercase tracking-widest text-[10px]">
                {t('actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {props.rows.map((d) => (
              <tr
                key={d.id}
                data-testid={`customer-row-${d.id}`}
                className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors group"
              >
                <td className="py-4 text-gray-400 dark:text-[#8b949e] font-mono text-xs">
                  {d.numberLabel}
                </td>
                <td className="py-4 font-black dark:text-[#f0f6fc] group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  <div>{d.name}</div>
                  <div className="text-[10px] text-gray-500 dark:text-[#8b949e] font-mono font-extrabold bg-gray-100 dark:bg-gray-800/40 px-2 py-0.5 rounded w-max mt-1 border border-gray-200 dark:border-[#30363d]/30 inline-flex items-center gap-1">
                    <span>💳</span>
                    <span>{d.accountNumber}</span>
                  </div>
                </td>
                <td className="py-4 text-gray-500 dark:text-[#8b949e] text-sm font-bold font-mono">
                  {d.phone || "-"}
                </td>
                <td className="py-4 text-gray-500 dark:text-[#8b949e] text-sm font-bold">
                  {d.address || "-"}
                </td>
                <td className="py-4 font-black text-orange-600 dark:text-orange-400 font-mono dark:digital-glow">
                  <div>{d.iqdLabel}</div>
                  {showExchangeRate && d.usdLabel !== null && (
                    <div className="text-[10px] text-blue-500 dark:text-blue-400">
                      {d.usdLabel}
                    </div>
                  )}
                </td>
                <td className="py-4">
                  <span
                    className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                      d.kind === "debt"
                        ? "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400"
                        : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                    }`}
                  >
                    {d.kind === "debt" ? t('owed') : t('credit')}
                  </span>
                </td>
                <td className="py-4 text-gray-400 dark:text-[#8b949e] text-xs font-bold font-mono">
                  {d.activity ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200 font-mono font-bold">
                        <Clock size={12} className="text-amber-500 shrink-0" />
                        <span>{d.activity.dateLabel}</span>
                      </div>
                      <div
                        className="inline-flex items-center gap-1 text-[10px] font-black text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded-md border border-amber-200 dark:border-amber-800/40 max-w-[190px] truncate"
                        title={d.activity.title}
                      >
                        <Activity size={10} className="shrink-0 text-amber-500" />
                        <span className="truncate">{d.activity.label}</span>
                      </div>
                    </div>
                  ) : (
                    d.createdLabel
                  )}
                </td>
                <td className="py-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => props.onStatement(d.id)}
                      data-testid={`customer-statement-${d.id}`}
                      className="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors flex items-center gap-1.5 border border-indigo-100 dark:border-indigo-900/30"
                      title={t('accountStatement')}
                    >
                      <FileText size={14} />
                      {t('cust.statement_btn')}
                    </button>
                    <button
                      onClick={() => props.onSharePdf(d.id)}
                      data-testid={`customer-share-${d.id}`}
                      className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors flex items-center gap-1.5 border border-emerald-100 dark:border-emerald-900/30"
                      title={t('cust.share_pdf')}
                    >
                      <Printer size={14} />
                      {t('cust.share_pdf')}
                    </button>
                    <button
                      onClick={() => props.onTransferFrom(d.id)}
                      data-testid={`customer-transfer-${d.id}`}
                      className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-600/20 flex items-center gap-1.5"
                      title={t('customerTransfer')}
                    >
                      <ArrowLeftRight size={14} />
                      {t('cust.transfer_row')}
                    </button>
                    {showExchangeRate && (
                      <button
                        onClick={() => props.onConvert(d.id)}
                        data-testid={`customer-convert-${d.id}`}
                        className="bg-amber-500 hover:bg-amber-600 dark:bg-amber-600/90 dark:hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:scale-105 active:scale-95 transition-all shadow-lg shadow-amber-500/20 flex items-center gap-1.5 border border-amber-400/30"
                        title={t('cust.convert_title')}
                      >
                        <ArrowLeftRight size={14} />
                        <span>$ ⇄ د.ع</span>
                      </button>
                    )}
                    {d.kind === "trust" ? (
                      <>
                        <button
                          onClick={() => props.onAdjust(d.id, "add")}
                          data-testid={`customer-add-${d.id}`}
                          className="bg-emerald-600 text-white px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/20"
                        >
                          {t('addTrust')}
                        </button>
                        <button
                          onClick={() => props.onAdjust(d.id, "withdraw")}
                          data-testid={`customer-withdraw-${d.id}`}
                          className="bg-rose-600 text-white px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-rose-700 transition-colors shadow-lg shadow-rose-600/20"
                        >
                          {t('withdrawTrust')}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => props.onAdjust(d.id, "add")}
                          data-testid={`customer-add-${d.id}`}
                          className="bg-rose-600 text-white px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-rose-700 transition-colors shadow-lg shadow-rose-600/20"
                        >
                          {t('addDebt')}
                        </button>
                        <button
                          onClick={() => props.onAdjust(d.id, "withdraw")}
                          data-testid={`customer-withdraw-${d.id}`}
                          className="bg-emerald-600 text-white px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/20"
                        >
                          {t('repayDebt')}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => props.onSettle(d.id)}
                      data-testid={`customer-settle-${d.id}`}
                      className="bg-gray-100 dark:bg-[#05070a] text-gray-600 dark:text-[#8b949e] px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-gray-200 dark:hover:bg-[#161b22] transition-colors border border-gray-200 dark:border-[#30363d]"
                    >
                      {t('settle')}
                    </button>
                    {props.canEdit && (
                      <button
                        onClick={() => props.onEdit(d.id)}
                        data-testid={`customer-edit-${d.id}`}
                        className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors border border-blue-100 dark:border-blue-900/30"
                        title={t('edit')}
                      >
                        <Edit2 size={12} />
                      </button>
                    )}
                    {props.canArchive && (
                      <button
                        onClick={() => props.onArchive(d.id)}
                        data-testid={`customer-archive-${d.id}`}
                        className="bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors border border-rose-100 dark:border-rose-900/30"
                        title={t('cust.archive_title')}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}
